import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  PlayCircle, Trash2, CheckCircle2, AlertTriangle, Plus, Pencil,
  ChevronUp, ChevronDown, Eye, EyeOff, ListChecks, X, Save, Users
} from "lucide-react";
import VideoUpload from "../../components/VideoUpload";
import LessonVideo from "../../components/LessonVideo";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const err = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || "").filter(Boolean).join("; ");
  return e?.message || "Ошибка";
};

const card = { background: "white", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 };
const label = { fontFamily: "Manrope", fontSize: 11, fontWeight: 700, color: "#6b7280",
                letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6 };
const btnGhost = { background: "white", color: "#0A0A0A", border: "1px solid #d1d5db",
                   borderRadius: 6, padding: "7px 11px", cursor: "pointer",
                   fontFamily: "Manrope", fontWeight: 600, fontSize: 12,
                   display: "inline-flex", alignItems: "center", gap: 6 };
const btnGold = { background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
                  border: "none", borderRadius: 6, padding: "9px 16px", cursor: "pointer",
                  fontFamily: "Manrope", fontWeight: 700, fontSize: 12.5,
                  display: "inline-flex", alignItems: "center", gap: 6 };
const input = { width: "100%", padding: "9px 11px", border: "1px solid #e5e7eb",
                borderRadius: 6, fontFamily: "Manrope", fontSize: 13, outline: "none",
                background: "white", color: "#0A0A0A", boxSizing: "border-box" };

const fmtSize = (n) => (!n ? "—" : n < 1024 * 1024
  ? `${(n / 1024).toFixed(0)} КБ` : `${(n / (1024 * 1024)).toFixed(1)} МБ`);
const fmtDur = (s) => (!s ? "—" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);

const ROLE_RU = { trainee: "стажёр", manager: "менеджер", senior_manager: "старший" };
const ROLE_OPTIONS = [
  { id: "trainee", label: "Стажёры" },
  { id: "manager", label: "Менеджеры" },
  { id: "senior_manager", label: "Старшие менеджеры" },
];

export default function AdminVideoLessons() {
  const [modules, setModules] = useState([]);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [preview, setPreview] = useState(null);
  const [openModule, setOpenModule] = useState(null);
  const [topicForm, setTopicForm] = useState(null);   // {id?, title, description, roles[], is_published}
  const [lessonForm, setLessonForm] = useState(null); // {id?, module_id, title, content_md, is_published}
  const [testFor, setTestFor] = useState(null);       // lesson object

  const load = async () => {
    try {
      const [b, r] = await Promise.all([
        axios.get(`${API}/admin/academy/builder`, { withCredentials: true }),
        axios.get(`${API}/admin/academy/video-report`, { withCredentials: true }),
      ]);
      setModules(b.data.modules || []);
      setReport(r.data);
      setOpenModule(prev => prev || (b.data.modules || [])[0]?.id || null);
    } catch (e) { setError(err(e)); }
  };

  useEffect(() => { load(); }, []);

  const saveTopic = async () => {
    const body = {
      title: topicForm.title.trim(),
      description: topicForm.description || "",
      roles: topicForm.roles || [],
      is_published: !!topicForm.is_published,
      free_access: true,     // свободный порядок прохождения
      order: topicForm.order || 0,
    };
    try {
      if (topicForm.id) {
        await axios.patch(`${API}/admin/academy/modules/${topicForm.id}`, body, { withCredentials: true });
      } else {
        const r = await axios.post(`${API}/admin/academy/modules`, body, { withCredentials: true });
        setOpenModule(r.data.id);
      }
      setTopicForm(null); setNotice("Тема сохранена"); await load();
    } catch (e) { setError(err(e)); }
  };

  const deleteTopic = async (m) => {
    if (!window.confirm(`Удалить тему «${m.title}» вместе с уроками и видео?`)) return;
    try {
      await axios.delete(`${API}/admin/academy/modules/${m.id}`, { withCredentials: true });
      setNotice("Тема удалена"); await load();
    } catch (e) { setError(err(e)); }
  };

  const moveTopic = async (m, dir) => {
    const sorted = [...modules].sort((a, b) => a.order - b.order);
    const i = sorted.findIndex(x => x.id === m.id);
    const j = i + dir;
    if (j < 0 || j >= sorted.length) return;
    try {
      await Promise.all([
        axios.patch(`${API}/admin/academy/modules/${m.id}`,
          { title: m.title, description: m.description, roles: m.roles,
            is_published: m.is_published, free_access: m.free_access, order: sorted[j].order },
          { withCredentials: true }),
        axios.patch(`${API}/admin/academy/modules/${sorted[j].id}`,
          { title: sorted[j].title, description: sorted[j].description, roles: sorted[j].roles,
            is_published: sorted[j].is_published, free_access: sorted[j].free_access, order: m.order },
          { withCredentials: true }),
      ]);
      await load();
    } catch (e) { setError(err(e)); }
  };

  const lessonPayload = (l, patch = {}) => ({
    module_id: patch.module_id || l.module_id,
    title: l.title, content_md: l.content_md || "",
    order: l.order || 0, is_published: l.is_published !== false,
    min_test_score: l.min_test_score || 80,
    video_required_percent: l.required_percent || 95,
    test: (l.test || []).map(q => ({ ...q, options: q.options || [] })),
    ...patch,
  });

  const saveLesson = async () => {
    const body = {
      module_id: lessonForm.module_id,
      title: lessonForm.title.trim(),
      content_md: lessonForm.content_md || "",
      is_published: !!lessonForm.is_published,
      order: lessonForm.order || 0,
      video_required_percent: 95,
      test: lessonForm.test || [],
      min_test_score: lessonForm.min_test_score || 80,
    };
    try {
      if (lessonForm.id) {
        await axios.patch(`${API}/admin/academy/lessons/${lessonForm.id}`, body, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/academy/lessons`, body, { withCredentials: true });
      }
      setLessonForm(null); setNotice("Урок сохранён"); await load();
    } catch (e) { setError(err(e)); }
  };

  const deleteLesson = async (l) => {
    if (!window.confirm(`Удалить урок «${l.title}»?`)) return;
    try {
      await axios.delete(`${API}/admin/academy/lessons/${l.id}`, { withCredentials: true });
      setNotice("Урок удалён"); await load();
    } catch (e) { setError(err(e)); }
  };

  const toggleLesson = async (m, l) => {
    try {
      await axios.patch(`${API}/admin/academy/lessons/${l.id}`,
        lessonPayload({ ...l, module_id: m.id }, { is_published: !l.is_published }),
        { withCredentials: true });
      await load();
    } catch (e) { setError(err(e)); }
  };

  const moveLesson = async (m, l, dir) => {
    const i = m.lessons.findIndex(x => x.id === l.id);
    const j = i + dir;
    if (j < 0 || j >= m.lessons.length) return;
    const other = m.lessons[j];
    try {
      await Promise.all([
        axios.patch(`${API}/admin/academy/lessons/${l.id}`,
          lessonPayload({ ...l, module_id: m.id }, { order: other.order }), { withCredentials: true }),
        axios.patch(`${API}/admin/academy/lessons/${other.id}`,
          lessonPayload({ ...other, module_id: m.id }, { order: l.order }), { withCredentials: true }),
      ]);
      await load();
    } catch (e) { setError(err(e)); }
  };

  const attach = async (lessonId, v) => {
    setError(""); setNotice("");
    try {
      await axios.post(`${API}/admin/academy/video/attach`, {
        lesson_id: lessonId, url: v.url, poster: v.poster,
        duration: v.duration, size: v.size, filename: v.original_name,
      }, { withCredentials: true });
      setNotice("Видео загружено — сотрудники уже могут его посмотреть");
      await load();
    } catch (e) { setError(err(e)); }
  };

  const removeVideo = async (lessonId) => {
    if (!window.confirm("Удалить видео из этого урока?")) return;
    try {
      await axios.delete(`${API}/admin/academy/video/${lessonId}`, { withCredentials: true });
      setNotice("Видео удалено"); await load();
    } catch (e) { setError(err(e)); }
  };

  const saveTest = async (questions) => {
    const m = modules.find(x => x.id === testFor.module_id);
    try {
      await axios.patch(`${API}/admin/academy/lessons/${testFor.id}`,
        lessonPayload({ ...testFor, module_id: m?.id || testFor.module_id }, { test: questions }),
        { withCredentials: true });
      setTestFor(null); setNotice("Тест сохранён"); await load();
    } catch (e) { setError(err(e)); }
  };

  return (
    <div data-testid="video-lessons-page" style={{ padding: "4px 2px 40px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                     gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div>
          <h2 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 26, margin: "0 0 4px" }}>
            Видеоуроки — конструктор обучения
          </h2>
          <div style={{ fontFamily: "Manrope", fontSize: 12.5, color: "#8a8a89",
                         lineHeight: 1.5, maxWidth: 760 }}>
            Создайте тему, внутри — уроки, к каждому загрузите видео. Урок зачтётся
            после просмотра 95%. По желанию добавьте к уроку 2–5 вопросов.
            Темы видны сотрудникам в разделе «Академия» в приложении.
          </div>
        </div>
        <button data-testid="topic-new-btn" style={btnGold}
          onClick={() => setTopicForm({ title: "", description: "", roles: [], is_published: true })}>
          <Plus size={14} /> Новая тема
        </button>
      </div>

      {error && (
        <div data-testid="video-lessons-error" style={{ background: "#fef2f2",
              border: "1px solid #fecaca", borderRadius: 8, padding: "10px 12px",
              color: "#991b1b", fontFamily: "Manrope", fontSize: 12.5, marginBottom: 14 }}>
          {error}
        </div>
      )}
      {notice && (
        <div data-testid="video-lessons-notice" style={{ background: "#ecfdf5",
              border: "1px solid #a7f3d0", borderRadius: 8, padding: "10px 12px",
              color: "#065f46", fontFamily: "Manrope", fontSize: 12.5, marginBottom: 14 }}>
          {notice}
        </div>
      )}

      <div style={{ display: "grid", gap: 12 }}>
        {modules.map(m => (
          <div key={m.id} data-testid={`topic-${m.id}`} style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button data-testid={`topic-toggle-${m.id}`}
                onClick={() => setOpenModule(openModule === m.id ? null : m.id)}
                style={{ ...btnGhost, border: "none", padding: 0, fontSize: 14.5, fontWeight: 700 }}>
                {openModule === m.id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                {m.title}
              </button>
              <span style={{ fontFamily: "Manrope", fontSize: 11.5, color: "#9ca3af" }}>
                {m.lessons.length} урок(ов)
                {m.roles?.length ? ` · только: ${m.roles.map(r => ROLE_RU[r] || r).join(", ")}` : " · всем сотрудникам"}
                {m.is_published ? "" : " · черновик"}
                {m.is_custom ? "" : " · встроенный курс"}
              </span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button data-testid={`topic-up-${m.id}`} onClick={() => moveTopic(m, -1)} style={btnGhost}>
                  <ChevronUp size={13} />
                </button>
                <button data-testid={`topic-down-${m.id}`} onClick={() => moveTopic(m, 1)} style={btnGhost}>
                  <ChevronDown size={13} />
                </button>
                <button data-testid={`topic-edit-${m.id}`} style={btnGhost}
                  onClick={() => setTopicForm({ id: m.id, title: m.title, description: m.description,
                                                 roles: m.roles || [], is_published: m.is_published,
                                                 order: m.order })}>
                  <Pencil size={13} /> Тема
                </button>
                {m.is_custom && (
                  <button data-testid={`topic-delete-${m.id}`} onClick={() => deleteTopic(m)}
                    style={{ ...btnGhost, color: "#b91c1c", borderColor: "#fca5a5" }}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>

            {openModule === m.id && (
              <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
                {!m.lessons.length && (
                  <div style={{ fontFamily: "Manrope", fontSize: 12.5, color: "#9ca3af" }}>
                    В теме пока нет уроков — добавьте первый.
                  </div>
                )}
                {m.lessons.map((l, idx) => (
                  <div key={l.id} data-testid={`lesson-${l.id}`}
                    style={{ border: "1px solid #f1f5f9", borderRadius: 8, padding: 12,
                              background: l.is_published ? "white" : "#fafaf9" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {l.has_video
                        ? <CheckCircle2 size={15} color="#059669" />
                        : <AlertTriangle size={15} color="#d97706" />}
                      <div style={{ fontFamily: "Manrope", fontSize: 13.5, fontWeight: 700 }}>
                        {idx + 1}. {l.title}
                      </div>
                      {!l.is_published && (
                        <span style={{ fontFamily: "Manrope", fontSize: 11, color: "#b45309" }}>черновик</span>
                      )}
                      <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button data-testid={`lesson-up-${l.id}`} onClick={() => moveLesson(m, l, -1)} style={btnGhost}>
                          <ChevronUp size={12} />
                        </button>
                        <button data-testid={`lesson-down-${l.id}`} onClick={() => moveLesson(m, l, 1)} style={btnGhost}>
                          <ChevronDown size={12} />
                        </button>
                        <button data-testid={`lesson-edit-${l.id}`} style={btnGhost}
                          onClick={() => setLessonForm({ id: l.id, module_id: m.id, title: l.title,
                                                          content_md: l.content_md, is_published: l.is_published,
                                                          order: l.order, test: l.test,
                                                          min_test_score: l.min_test_score })}>
                          <Pencil size={12} /> Правка
                        </button>
                        <button data-testid={`lesson-test-${l.id}`} style={btnGhost}
                          onClick={() => setTestFor({ ...l, module_id: m.id })}>
                          <ListChecks size={12} /> Тест ({l.test?.length || 0})
                        </button>
                        <button data-testid={`lesson-publish-${l.id}`} onClick={() => toggleLesson(m, l)} style={btnGhost}>
                          {l.is_published ? <EyeOff size={12} /> : <Eye size={12} />}
                        </button>
                        <button data-testid={`lesson-delete-${l.id}`} onClick={() => deleteLesson(l)}
                          style={{ ...btnGhost, color: "#b91c1c", borderColor: "#fca5a5" }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    {l.content_md && (
                      <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#4b5563",
                                     marginTop: 6, whiteSpace: "pre-wrap" }}>{l.content_md}</div>
                    )}
                    <div style={{ fontFamily: "Manrope", fontSize: 11.5, color: "#8a8a89", marginTop: 6 }}>
                      {l.has_video
                        ? `${l.video_filename || "видео"} · ${fmtSize(l.video_size)} · ${fmtDur(l.video_duration)}`
                        : "Видео ещё не загружено"} · зачёт с {l.required_percent}% ·
                      посмотрели: {l.watched_count} · прошли: {l.completed_count}
                    </div>

                    {l.has_video ? (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                        <button data-testid={`video-preview-${l.id}`}
                          onClick={() => setPreview(preview === l.id ? null : l.id)} style={btnGhost}>
                          <PlayCircle size={13} /> {preview === l.id ? "Скрыть" : "Посмотреть"}
                        </button>
                        <button data-testid={`video-delete-${l.id}`} onClick={() => removeVideo(l.id)}
                          style={{ ...btnGhost, color: "#b91c1c", borderColor: "#fca5a5" }}>
                          <Trash2 size={13} /> Удалить видео
                        </button>
                      </div>
                    ) : (
                      <div style={{ marginTop: 8 }}>
                        <VideoUpload value={[]} maxVideos={1}
                          testId={`video-upload-${l.id}`}
                          label="Файл урока"
                          hint="MP4 / MOV, до 1 ГБ. Загрузка идёт напрямую в облако."
                          onChange={(arr) => { const v = arr[arr.length - 1]; if (v) attach(l.id, v); }} />
                      </div>
                    )}

                    {preview === l.id && l.video_url && (
                      <div style={{ marginTop: 12 }}>
                        <LessonVideo lessonId={l.id} videoUrl={l.video_url}
                          requiredPercent={l.required_percent} track={false} />
                      </div>
                    )}
                  </div>
                ))}
                <button data-testid={`lesson-new-${m.id}`} style={{ ...btnGhost, width: "fit-content" }}
                  onClick={() => setLessonForm({ module_id: m.id, title: "", content_md: "",
                                                  is_published: true, test: [] })}>
                  <Plus size={13} /> Добавить урок
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Кто прошёл */}
      <div style={{ ...card, marginTop: 18, overflowX: "auto" }}>
        <div style={label}><Users size={12} style={{ verticalAlign: "-2px" }} /> Кто прошёл видеоуроки</div>
        {!report?.staff?.length && (
          <div style={{ fontFamily: "Manrope", fontSize: 12.5, color: "#9ca3af" }}>
            Пока нет загруженных видео или сотрудников для отчёта
          </div>
        )}
        {!!report?.staff?.length && (
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
            <thead>
              <tr>
                <th style={{ ...label, textAlign: "left", padding: "6px 8px" }}>Сотрудник</th>
                {report.lessons.map((l, i) => (
                  <th key={l.id} title={l.title}
                      style={{ ...label, textAlign: "center", padding: "6px 8px" }}>
                    {i + 1}
                  </th>
                ))}
                <th style={{ ...label, textAlign: "center", padding: "6px 8px" }}>Итого</th>
              </tr>
            </thead>
            <tbody>
              {report.staff.map(s => (
                <tr key={s.id} data-testid={`video-report-row-${s.id}`}
                  style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={{ fontFamily: "Manrope", fontSize: 12.5, padding: "7px 8px" }}>
                    {s.name}
                    <span style={{ color: "#9ca3af" }}> · {ROLE_RU[s.role] || s.role}</span>
                  </td>
                  {report.lessons.map(l => {
                    const p = s.lessons[l.id] || {};
                    return (
                      <td key={l.id} style={{ textAlign: "center", fontFamily: "Manrope",
                                                fontSize: 12, padding: "7px 8px",
                                                color: p.completed ? "#047857"
                                                     : p.watch_percent ? "#b45309" : "#9ca3af" }}>
                        {p.completed ? `✓ ${p.best_score}%`
                          : p.watch_percent ? `${p.watch_percent}%` : "—"}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: "center", fontFamily: "Manrope", fontSize: 12,
                                fontWeight: 700,
                                color: s.done === report.total_lessons ? "#047857" : "#6b7280",
                                padding: "7px 8px" }}>
                    {s.done}/{report.total_lessons}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {topicForm && (
        <Modal title={topicForm.id ? "Тема обучения" : "Новая тема"} onClose={() => setTopicForm(null)}>
          <Field label="Название темы">
            <input data-testid="topic-title" style={input} value={topicForm.title}
              onChange={e => setTopicForm({ ...topicForm, title: e.target.value })}
              placeholder="Например: Работа с клиентом офлайн в бутике" />
          </Field>
          <Field label="Описание (необязательно)">
            <textarea data-testid="topic-description" rows={3}
              style={{ ...input, resize: "vertical" }} value={topicForm.description}
              onChange={e => setTopicForm({ ...topicForm, description: e.target.value })} />
          </Field>
          <Field label="Кому показывать (ничего не отмечено = всем сотрудникам)">
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {ROLE_OPTIONS.map(r => (
                <label key={r.id} style={{ fontFamily: "Manrope", fontSize: 12.5,
                                            display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" data-testid={`topic-role-${r.id}`}
                    checked={(topicForm.roles || []).includes(r.id)}
                    onChange={e => setTopicForm({
                      ...topicForm,
                      roles: e.target.checked
                        ? [...(topicForm.roles || []), r.id]
                        : (topicForm.roles || []).filter(x => x !== r.id),
                    })} />
                  {r.label}
                </label>
              ))}
            </div>
          </Field>
          <label style={{ fontFamily: "Manrope", fontSize: 12.5, display: "flex",
                           alignItems: "center", gap: 6 }}>
            <input type="checkbox" data-testid="topic-published" checked={!!topicForm.is_published}
              onChange={e => setTopicForm({ ...topicForm, is_published: e.target.checked })} />
            Опубликовать тему (видна сотрудникам)
          </label>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <button style={btnGhost} onClick={() => setTopicForm(null)}>Отмена</button>
            <button data-testid="topic-save-btn" style={{ ...btnGold, opacity: topicForm.title.trim() ? 1 : 0.5 }}
              disabled={!topicForm.title.trim()} onClick={saveTopic}>
              <Save size={14} /> Сохранить
            </button>
          </div>
        </Modal>
      )}

      {lessonForm && (
        <Modal title={lessonForm.id ? "Урок" : "Новый урок"} onClose={() => setLessonForm(null)}>
          <Field label="Название урока">
            <input data-testid="lesson-title" style={input} value={lessonForm.title}
              onChange={e => setLessonForm({ ...lessonForm, title: e.target.value })}
              placeholder="Например: Встреча клиента и первый контакт" />
          </Field>
          <Field label="Что в уроке (текст под видео, необязательно)">
            <textarea data-testid="lesson-content" rows={4}
              style={{ ...input, resize: "vertical" }} value={lessonForm.content_md}
              onChange={e => setLessonForm({ ...lessonForm, content_md: e.target.value })} />
          </Field>
          <label style={{ fontFamily: "Manrope", fontSize: 12.5, display: "flex",
                           alignItems: "center", gap: 6 }}>
            <input type="checkbox" data-testid="lesson-published" checked={!!lessonForm.is_published}
              onChange={e => setLessonForm({ ...lessonForm, is_published: e.target.checked })} />
            Опубликовать урок
          </label>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <button style={btnGhost} onClick={() => setLessonForm(null)}>Отмена</button>
            <button data-testid="lesson-save-btn" style={{ ...btnGold, opacity: lessonForm.title.trim() ? 1 : 0.5 }}
              disabled={!lessonForm.title.trim()} onClick={saveLesson}>
              <Save size={14} /> Сохранить
            </button>
          </div>
        </Modal>
      )}

      {testFor && (
        <TestEditor lesson={testFor} onClose={() => setTestFor(null)} onSave={saveTest} />
      )}
    </div>
  );
}

function TestEditor({ lesson, onClose, onSave }) {
  const [questions, setQuestions] = useState(
    (lesson.test || []).map(q => ({ ...q, options: q.options?.length ? q.options : ["", ""] })));

  const addQ = () => setQuestions([...questions, {
    id: `q${Date.now()}`, question: "", options: ["", ""], correct_index: 0, explanation: "",
  }]);
  const upd = (i, patch) => setQuestions(questions.map((q, idx) => idx === i ? { ...q, ...patch } : q));

  const valid = questions.every(q => q.question.trim()
    && q.options.filter(o => o.trim()).length >= 2) && questions.length <= 5;

  return (
    <Modal title={`Тест к уроку «${lesson.title}»`} onClose={onClose} wide>
      <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#8a8a89", marginBottom: 12 }}>
        2–5 вопросов. Тест откроется сотруднику после просмотра {lesson.required_percent}% видео.
        Если вопросов нет — урок зачтётся по просмотру.
      </div>
      <div style={{ display: "grid", gap: 14 }}>
        {questions.map((q, i) => (
          <div key={q.id} data-testid={`test-q-${i}`}
            style={{ border: "1px solid #f1f5f9", borderRadius: 8, padding: 12 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
              <div style={{ ...label, margin: 0 }}>Вопрос {i + 1}</div>
              <button data-testid={`test-q-del-${i}`} style={{ ...btnGhost, marginLeft: "auto",
                        color: "#b91c1c", borderColor: "#fca5a5" }}
                onClick={() => setQuestions(questions.filter((_, idx) => idx !== i))}>
                <Trash2 size={12} />
              </button>
            </div>
            <input data-testid={`test-q-text-${i}`} style={input} value={q.question}
              placeholder="Текст вопроса"
              onChange={e => upd(i, { question: e.target.value })} />
            <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
              {q.options.map((opt, oi) => (
                <label key={oi} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="radio" data-testid={`test-q-${i}-correct-${oi}`}
                    checked={q.correct_index === oi}
                    onChange={() => upd(i, { correct_index: oi })} />
                  <input data-testid={`test-q-${i}-option-${oi}`} style={input} value={opt}
                    placeholder={`Вариант ${oi + 1}${oi === q.correct_index ? " (правильный)" : ""}`}
                    onChange={e => upd(i, {
                      options: q.options.map((o, idx) => idx === oi ? e.target.value : o),
                    })} />
                  {q.options.length > 2 && (
                    <button style={{ ...btnGhost, padding: "6px 8px" }}
                      onClick={() => upd(i, {
                        options: q.options.filter((_, idx) => idx !== oi),
                        correct_index: Math.min(q.correct_index, q.options.length - 2),
                      })}>
                      <X size={12} />
                    </button>
                  )}
                </label>
              ))}
              {q.options.length < 4 && (
                <button data-testid={`test-q-${i}-add-option`} style={{ ...btnGhost, width: "fit-content" }}
                  onClick={() => upd(i, { options: [...q.options, ""] })}>
                  <Plus size={12} /> Вариант ответа
                </button>
              )}
            </div>
            <input data-testid={`test-q-expl-${i}`} style={{ ...input, marginTop: 8 }}
              value={q.explanation || ""} placeholder="Пояснение к правильному ответу (необязательно)"
              onChange={e => upd(i, { explanation: e.target.value })} />
          </div>
        ))}
      </div>
      {questions.length < 5 && (
        <button data-testid="test-add-q" style={{ ...btnGhost, marginTop: 12 }} onClick={addQ}>
          <Plus size={13} /> Добавить вопрос
        </button>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
        <button style={btnGhost} onClick={onClose}>Отмена</button>
        <button data-testid="test-save-btn" style={{ ...btnGold, opacity: valid ? 1 : 0.5 }}
          disabled={!valid}
          onClick={() => onSave(questions.map(q => ({
            id: q.id, question: q.question.trim(),
            options: q.options.filter(o => o.trim()),
            correct_index: q.correct_index, explanation: q.explanation || "",
          })))}>
          <Save size={14} /> Сохранить тест
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 120,
                   display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
                   overflowY: "auto" }}>
      <div style={{ background: "white", borderRadius: 10, padding: 22, width: wide ? 680 : 520,
                     maxWidth: "100%", maxHeight: "92vh", overflowY: "auto", color: "#0A0A0A" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                       marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontFamily: "Manrope", fontWeight: 700, fontSize: 16.5 }}>{title}</h3>
          <button onClick={onClose} data-testid="modal-close"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#8a8a89" }}>
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label: text, children }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <div style={{ ...label, color: "#0A0A0A" }}>{text}</div>
      {children}
    </label>
  );
}
